/**
 * CR500 Phase 2B: EvidenceBlock
 * 
 * Renders evidence links (proof screenshots, source URLs) in deal details.
 */

import React from "react";
import { Eye, Image as ImageIcon, ExternalLink } from "lucide-react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";

export const EvidenceBlock: React.FC<DealCardBlockProps<Deal>> = ({
  deal,
  block,
  context,
}) => {
  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  const proofUrls = deal.proof_urls || [];
  const hasEvidence = proofUrls.length > 0;
  
  // If no evidence, don't render
  if (!hasEvidence) {
    return null;
  }

  return (
    <div 
      className="space-y-2"
      data-block="evidence"
      data-block-id={block.id}
    >
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
        <Eye className="w-3 h-3" />
        {context.labels?.evidence_title || "Evidence"}
      </h4>
      
      <div className="flex flex-wrap gap-2">
        {proofUrls.map((url, index) => (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-muted text-muted-foreground hover:bg-muted/80 rounded-full transition-colors"
          >
            <ImageIcon className="w-3 h-3" />
            {proofUrls.length === 1 ? "View proof" : `Proof ${index + 1}`}
            <ExternalLink className="w-3 h-3" />
          </a>
        ))}
      </div>
    </div>
  );
};

export default EvidenceBlock;
